import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '../i18n';

type Variant = 'navbar' | 'sidebar' | 'topbar';

interface Props {
    variant?: Variant;
}

/**
 * Compact language selector. Stores choice in localStorage via i18next-browser-languagedetector.
 * Variants:
 *  - navbar  → pill with globe + short code, used on the marketing navbar
 *  - sidebar → full-width row, used inside dashboard sidebars (footer area)
 *  - topbar  → small icon button, used inside dashboard topbars
 */
export default function LanguageSwitcher({ variant = 'navbar' }: Props) {
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    const active =
        SUPPORTED_LANGUAGES.find((l) => l.code === i18n.resolvedLanguage) ||
        SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language?.split('-')[0]) ||
        SUPPORTED_LANGUAGES[0];

    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        window.addEventListener('mousedown', onClick);
        return () => window.removeEventListener('mousedown', onClick);
    }, [open]);

    const select = (code: string) => {
        void i18n.changeLanguage(code);
        setOpen(false);
    };

    const triggerStyle: React.CSSProperties =
        variant === 'navbar'
            ? {
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '0.4rem 0.65rem',
                  borderRadius: 999,
                  border: '1px solid rgba(126, 108, 192, 0.25)',
                  background: 'rgba(126, 108, 192, 0.06)',
                  color: '#4f46e5',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background 0.15s, border-color 0.15s',
              }
            : variant === 'sidebar'
            ? {
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 10,
                  border: '1px solid var(--border-color, rgba(0,0,0,0.08))',
                  background: 'transparent',
                  color: 'inherit',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
              }
            : {
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '0.4rem 0.55rem',
                  borderRadius: 8,
                  border: '1px solid transparent',
                  background: 'transparent',
                  color: 'var(--color-text-secondary, inherit)',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'background 0.15s, border-color 0.15s, color 0.15s',
              };

    return (
        <div ref={rootRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
                style={triggerStyle}
            >
                {variant === 'navbar' ? (
                    <>
                        <Globe size={14} />
                        <span>{active.flag}</span>
                        <span style={{ textTransform: 'uppercase' }}>{active.code}</span>
                    </>
                ) : variant === 'sidebar' ? (
                    <>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <Globe size={15} />
                            <span>{active.flag}</span>
                            <span>{active.label}</span>
                        </span>
                        <ChevronDown size={14} />
                    </>
                ) : (
                    <>
                        <Globe size={15} />
                        <span>{active.flag}</span>
                    </>
                )}
            </button>

            {open && (
                <ul
                    role="listbox"
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: 'calc(100% + 6px)',
                        minWidth: 180,
                        margin: 0,
                        padding: '0.35rem',
                        listStyle: 'none',
                        background: '#fff',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        borderRadius: 10,
                        boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
                        zIndex: 300,
                    }}
                >
                    {SUPPORTED_LANGUAGES.map((lang) => {
                        const selected = lang.code === active.code;
                        return (
                            <li key={lang.code}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={selected}
                                    onClick={() => select(lang.code)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 10,
                                        width: '100%',
                                        padding: '0.5rem 0.65rem',
                                        background: selected ? 'rgba(126, 108, 192, 0.08)' : 'transparent',
                                        border: 'none',
                                        borderRadius: 8,
                                        cursor: 'pointer',
                                        fontSize: '0.875rem',
                                        fontWeight: selected ? 600 : 500,
                                        color: '#111827',
                                        textAlign: 'left',
                                    }}
                                >
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                                        <span style={{ fontSize: '1.05rem' }}>{lang.flag}</span>
                                        <span>{lang.label}</span>
                                    </span>
                                    {selected && <Check size={14} color="#4f46e5" />}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
